import api from './api';
import {
  normalizeChannelPreferences,
  DEFAULT_CHANNEL_PREFERENCES
} from '../utils/notificationChannelPreferences';

const LEGACY_STORAGE_KEY = 'ucp_user_preferences';
const CACHE_STORAGE_KEY = 'ucp_user_preferences_cache';

const preferenceListeners = new Set();

export const subscribeUserPreferences = (listener) => {
  if (typeof listener !== 'function') return () => {};
  preferenceListeners.add(listener);
  return () => preferenceListeners.delete(listener);
};

const notifyPreferenceListeners = (prefs) => {
  preferenceListeners.forEach((listener) => {
    try {
      listener(prefs);
    } catch (error) {
      console.warn('[userPreferences] Listener error:', error);
    }
  });
};

const DEFAULT_PREFERENCES = {
  theme: 'light',
  language: 'fr',
  ...DEFAULT_CHANNEL_PREFERENCES
};

const sanitizeTheme = (value) => (['light', 'dark', 'auto'].includes(value) ? value : 'light');
const sanitizeLanguage = (value) => (['fr', 'en', 'es'].includes(value) ? value : 'fr');

const toBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['true', '1', 'yes', 'oui', 'on'].includes(normalized);
  }
  return Boolean(value);
};

const normalizePrefs = (raw = {}) => ({
  theme: sanitizeTheme(raw.theme),
  language: sanitizeLanguage(raw.language),
  ...normalizeChannelPreferences(raw)
});

const readLegacyPreferences = () => {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    return normalizePrefs(JSON.parse(raw));
  } catch {
    return null;
  }
};

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.userId || !parsed?.role || !parsed?.preferences) return null;
    return {
      userId: Number(parsed.userId),
      role: String(parsed.role),
      preferences: normalizePrefs(parsed.preferences)
    };
  } catch {
    return null;
  }
};

const writeCache = (userId, role, preferences) => {
  if (!Number.isFinite(Number(userId)) || !role) return;
  localStorage.setItem(
    CACHE_STORAGE_KEY,
    JSON.stringify({
      userId: Number(userId),
      role: String(role),
      preferences: normalizePrefs(preferences)
    })
  );
  localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(normalizePrefs(preferences)));
};

export const clearUserPreferencesCache = () => {
  localStorage.removeItem(CACHE_STORAGE_KEY);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
};

export const getCachedPreferencesForUser = (user) => {
  if (!user?.id || !user?.role) return null;
  const cache = readCache();
  if (!cache) return null;
  if (Number(cache.userId) !== Number(user.id)) return null;
  if (String(cache.role) !== String(user.role)) return null;
  return cache.preferences;
};

export const getUserPreferences = () => {
  const cache = readCache();
  if (cache?.preferences) return cache.preferences;
  return readLegacyPreferences() || { ...DEFAULT_PREFERENCES };
};

export const applyUserPreferences = (preferences) => {
  const safe = normalizePrefs({
    ...DEFAULT_PREFERENCES,
    ...preferences
  });

  const activeTheme = resolveTheme(safe.theme);

  document.documentElement.setAttribute('data-theme', activeTheme);
  document.documentElement.setAttribute('data-theme-mode', safe.theme);
  document.documentElement.setAttribute('lang', safe.language);
  document.body.setAttribute('data-theme', activeTheme);

  return safe;
};

export const fetchRemoteUserPreferences = async () => {
  const response = await api.get('/me/preferences');
  const prefs = normalizePrefs(response.data?.preferences || response.data);
  return prefs;
};

/**
 * Applique les préférences du compte connecté (login ou rechargement session).
 */
export const applyPreferencesForUser = (user, preferences) => {
  const safe = normalizePrefs(preferences || DEFAULT_PREFERENCES);
  if (user?.id && user?.role) {
    writeCache(user.id, user.role, safe);
  }
  applyUserPreferences(safe);
  notifyPreferenceListeners(safe);
  return safe;
};

/**
 * Charge depuis l'API et applique pour l'utilisateur courant.
 */
export const loadAndApplyUserPreferences = async (user) => {
  if (!user?.id || !user?.role) {
    return applyUserPreferences(getUserPreferences());
  }
  try {
    const remote = await fetchRemoteUserPreferences();
    return applyPreferencesForUser(user, remote);
  } catch (error) {
    console.warn('[userPreferences] Chargement distant impossible, cache local:', error?.message);
    const cached = getCachedPreferencesForUser(user);
    if (cached) return applyPreferencesForUser(user, cached);
    const legacy = readLegacyPreferences();
    if (legacy) return applyPreferencesForUser(user, legacy);
    return applyPreferencesForUser(user, DEFAULT_PREFERENCES);
  }
};

export const saveUserPreferences = async (partialPreferences = {}, options = {}) => {
  const { user = null, syncRemote = true } = options;
  const merged = normalizePrefs({
    ...getUserPreferences(),
    ...partialPreferences
  });

  if (user?.id && user?.role) {
    writeCache(user.id, user.role, merged);
  } else {
    localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(merged));
  }

  applyUserPreferences(merged);
  notifyPreferenceListeners(merged);

  if (syncRemote && user?.id && user?.role) {
    try {
      const response = await api.put('/me/preferences', merged);
      const remote = normalizePrefs(response.data?.preferences || merged);
      writeCache(user.id, user.role, remote);
      applyUserPreferences(remote);
      notifyPreferenceListeners(remote);
      return remote;
    } catch (error) {
      console.warn('[userPreferences] Sync serveur échouée:', error?.message);
    }
  }

  return merged;
};

const resolveTheme = (theme) => {
  if (theme !== 'auto') return theme;
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export { resolveThemeMode } from '../styles/themePalettes';
